import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

// * MUI Icons
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';

// * Utils
import { getInitiativeInclusionsForNode } from '@/features/energyxplorer/utils/energyTreeNodeInitiativeInclusion';
import {
  getGroupedOptions,
  getTypeColor,
  getTypeIcon,
} from '@/utils/nodeTypeMapper';

import { mapUnitsToDataStreams } from '../../utils/energyTreeFunctions';

// ✅ New soft validation
import { validateAddNode } from '../../utils/energyTreeNodesValidationMiddleware';

// * Redux
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import { useDispatch, useSelector } from 'react-redux';

// * API
import { usePostEnergyTreeNodesMutation } from '@/api/recalcV2/endpoints/energyTreeNodesApiSlice';

import InitiativeInclusionNotice from './InitiativeInclusionNotice';
import ValidationNotice from './ValidationNotice';

const AddNodeDialog = ({
  open,
  onClose,
  clickedNode,
  energyGroup,
  tree,
  units,
}) => {
  const dispatch = useDispatch();
  const account = useSelector(getSelectedAccount);

  const inclusions = useMemo(() => {
    const nodesFound = getInitiativeInclusionsForNode(
      clickedNode.energyTreeNodeId,
      energyGroup,
    );
    return nodesFound;
  }, [clickedNode.energyTreeNodeId, energyGroup]);

  const virtualInitiativeInclusions = useMemo(
    () =>
      (inclusions || []).filter(
        ({ viaNode }) =>
          viaNode && (!viaNode.dataStreams || viaNode.dataStreams.length === 0),
      ),
    [inclusions],
  );

  const [postEnergyTreeNode] = usePostEnergyTreeNodesMutation();

  const [makeResidual, setMakeResidual] = useState(false);
  const [residualSource, setResidualSource] = useState('');
  const [searchText, setSearchText] = useState('');

  const [formState, setFormState] = useState(() => ({
    name: '',
    type: '',
    dataStreams: [],
    energyGroupId: energyGroup?.energyGroupId ?? null,
    parentId: clickedNode?.energyTreeNodeId ?? null,
    options: {
      specialNodeType: null,
      power: null,
      percentage: null,
    },
  }));

  useEffect(() => {
    if (open) {
      // Dialog just opened → sync relevant props
      setFormState({
        name: '',
        type: '',
        dataStreams: [],
        energyGroupId: energyGroup?.energyGroupId ?? null,
        parentId: clickedNode?.energyTreeNodeId ?? null,
        options: {
          specialNodeType: null,
          power: null,
          percentage: null,
        },
      });
      setMakeResidual(false);
      setResidualSource('');
      setSearchText('');
    } else {
      // Dialog just closed → reset completely
      setFormState({
        name: '',
        type: '',
        dataStreams: [],
        energyGroupId: null,
        parentId: null,
        options: {
          specialNodeType: null,
          power: null,
          percentage: null,
        },
      });
      setMakeResidual(false);
      setResidualSource('');
      setSearchText('');
    }
  }, [open, clickedNode?.energyTreeNodeId, energyGroup?.energyGroupId]);

  // ✅ Soft hierarchy validation for the current selection
  const validationResult = useMemo(() => {
    if (!clickedNode || !formState.type) return null;
    return validateAddNode({
      targetType: formState.type,
      parent: clickedNode,
      tree,
      dataStreams: formState.dataStreams,
      addingResidual: makeResidual,
    });
  }, [clickedNode, formState.type, formState.dataStreams, tree, makeResidual]);

  const hasErrors = validationResult?.errorMessages?.length > 0;

  const filteredOptionGroups = useMemo(() => {
    const groupedOptions = getGroupedOptions();
    // For now we keep filterOptionGroupsForAdd – it should be relatively loose.
    return groupedOptions;
  }, []);

  const handleAddNode = async () => {
    if (!formState.type) {
      dispatch(
        showSnackbar({
          message: 'Vælg venligst en node-type før oprettelse.',
          severity: 'warning',
        }),
      );
      return;
    }

    const payload = { ...formState };
    if (!makeResidual) {
      payload.options = null;
    }

    try {
      const createdNode = await postEnergyTreeNode({
        energyTreeNode: payload,
        accountId: account.AccountId,
      }).unwrap();
      dispatch(showSnackbar({ message: 'Node tilføjet', severity: 'success' }));
      onClose();
      return createdNode;
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i tilføjelse af node',
          severity: 'error',
        }),
      );
      return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" fontSize="1.25rem">
          {`Opret ny node under`}&nbsp;
          {clickedNode?.name}
          <Box
            sx={{
              backgroundColor: getTypeColor(clickedNode?.type),
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              ml: 1,
              height: 28,
              width: 28,
              justifyContent: 'center',
              fontSize: '14px',
            }}
          >
            <i className={`fa-solid ${getTypeIcon(clickedNode?.type)}`} />
          </Box>
        </Box>

        <i className="fa-solid fa-plus"></i>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mb={2}>
          <TextField
            label="Navn"
            value={formState.name}
            helperText="Indtast navnet på den nye node."
            onChange={(e) =>
              setFormState({ ...formState, name: e.target.value })
            }
            fullWidth
            variant="filled"
          />

          <TextField
            label="Node Type"
            select
            value={formState.type}
            onChange={(e) =>
              setFormState({ ...formState, type: e.target.value })
            }
            fullWidth
            helperText={
              formState.type && validationResult
                ? hasErrors
                  ? 'Fejl: ' + validationResult.errorMessages[0]
                  : 'Hierarki-hint: ' + validationResult.message
                : 'Vælg node type.'
            }
            variant="filled"
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: { maxHeight: 500 },
                },
              },
            }}
          >
            <MenuItem value="" disabled>
              <Typography color="text.secondary">Vælg type</Typography>
            </MenuItem>
            {Object.entries(filteredOptionGroups).map(
              ([groupLabel, options]) => {
                if (!options.length) return null;

                return [
                  <ListSubheader
                    key={`header-${groupLabel}`}
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    <ListItem divider disableGutters>
                      <ListItemIcon>
                        <CategoryRoundedIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={groupLabel}
                        primaryTypographyProps={{ fontWeight: 'bold' }}
                      />
                    </ListItem>
                  </ListSubheader>,
                  ...options.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      <Box display="flex" width="100%">
                        <Box
                          sx={{
                            backgroundColor: getTypeColor(value),
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            mr: 1,
                            height: 28,
                            width: 28,
                            justifyContent: 'center',
                            fontSize: '14px',
                          }}
                        >
                          <i className={`fa-solid ${getTypeIcon(value)}`} />
                        </Box>
                        <Typography>{label}</Typography>
                      </Box>
                    </MenuItem>
                  )),
                ];
              },
            )}
          </TextField>

          {!makeResidual && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={units}
              getOptionLabel={(option) => `${option.UnitId} - ${option.Name}`}
              value={formState.dataStreams.map((ds) =>
                units.find((unit) => unit.UnitId === ds.unitId),
              )}
              inputValue={searchText}
              onInputChange={(event, newInputValue, reason) => {
                if (reason !== 'reset') setSearchText(newInputValue);
              }}
              getOptionDisabled={(option) =>
                !!option.usedInNode || !!option.notSupportedReason
              }
              onClose={() => setSearchText('')}
              onChange={(e, newValue) => {
                setFormState({
                  ...formState,
                  dataStreams: mapUnitsToDataStreams(newValue),
                });
              }}
              filterSelectedOptions
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Typography>
                      ({option.UnitId}) {option.Name}
                    </Typography>
                    {option.usedInNode && (
                      <Typography variant="caption" sx={{ color: 'gray' }}>
                        Anvendt for: {option.usedInNode}
                      </Typography>
                    )}
                    {option.notSupportedReason && (
                      <Typography variant="caption" sx={{ color: 'red' }}>
                        {option.notSupportedReason}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Units"
                  variant="filled"
                  helperText="Vælg de Units som skal associeres med denne node. Disse enheder vil blive brugt i oversigten til at vise energiforbrugsdata, hvis det er tilgængeligt."
                  placeholder="Vælg units"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    sx={{ borderRadius: 1 }}
                    key={option.UnitId}
                    label={`${option.Name}`}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          )}

          <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={makeResidual}
                    onChange={() => {
                      setMakeResidual((prev) => {
                        const next = !prev;

                        if (!next) {
                          setFormState((fs) => ({
                            ...fs,
                            options: {
                              specialNodeType: null,
                              power: null,
                              percentage: null,
                            },
                          }));
                        }

                        if (next) {
                          setFormState((fs) => ({
                            ...fs,
                            dataStreams: [],
                            options: {
                              specialNodeType: 'RestResidual',
                              power: null,
                              percentage: null,
                            },
                          }));
                        }

                        return next;
                      });
                    }}
                    sx={{ mx: 1 }}
                  />
                }
                label="Opret som residual node"
              />
              <FormHelperText>
                Ved at oprette som en residual node, er det ikke muligt at
                associere datastrømme/units med denne node. I stedet kan man ved
                oprettelse af en residual node vælge om noden skal reflektere
                det resterende forbrug, eller om hvorvidt man anslår en fast
                effekt for noden, eller en procentvis andel.
              </FormHelperText>
            </FormGroup>

            {makeResidual && (
              <>
                <TextField
                  select
                  label="Rolle"
                  sx={{ mt: 2 }}
                  value={residualSource}
                  helperText="Vælg hvilken rolle denne node har. Dette påvirker hvordan det repræsenterede data fremgår."
                  onChange={(e) => {
                    const next = e.target.value;
                    setResidualSource(next);

                    setFormState((fs) => {
                      const updated = { ...fs };
                      updated.options.specialNodeType = next;
                      if (next !== 'FlatResidual') {
                        updated.options.power = null;
                      }
                      if (next !== 'PercentageResidual') {
                        updated.options.percentage = null;
                      }
                      return updated;
                    });
                  }}
                  fullWidth
                  variant="filled"
                >
                  <MenuItem value="RestResidual">Restforbrug</MenuItem>
                  <MenuItem value="FlatResidual">Fast effekt (kW)</MenuItem>
                  <MenuItem value="PercentageResidual">
                    Procentandel (%)
                  </MenuItem>
                </TextField>

                {residualSource === 'FlatResidual' && (
                  <TextField
                    label="Fast effekt (kW)"
                    type="number"
                    inputProps={{ min: 0, step: 'any' }}
                    value={formState?.options?.power || ''}
                    onChange={(e) => {
                      setFormState((fs) => ({
                        ...fs,
                        options: {
                          ...fs.options,
                          power:
                            e.target.value === ''
                              ? null
                              : Number(e.target.value),
                        },
                      }));
                    }}
                    variant="filled"
                    fullWidth
                    helperText="Angiv en fast residualeffekt i kW."
                  />
                )}

                {residualSource === 'PercentageResidual' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography gutterBottom>Procentandel (%)</Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <TextField
                        type="number"
                        variant="filled"
                        hiddenLabel
                        value={(
                          (formState?.options?.percentage ?? 0) * 100
                        ).toFixed(1)}
                        onChange={(e) => {
                          const pct = parseFloat(e.target.value);
                          const clamped = isNaN(pct)
                            ? 0
                            : Math.min(Math.max(pct, 0), 100);
                          setFormState((fs) => ({
                            ...fs,
                            options: {
                              ...fs.options,
                              percentage: clamped / 100,
                            },
                          }));
                        }}
                        inputProps={{
                          min: 0,
                          max: 100,
                          step: 0.1,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <PercentRoundedIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Slider
                        value={(formState?.options?.percentage || 0) * 100}
                        onChange={(_, newVal) =>
                          setFormState((fs) => ({
                            ...fs,
                            options: {
                              ...fs.options,
                              percentage: newVal / 100,
                            },
                          }))
                        }
                        min={0.1}
                        max={100}
                        step={0.1}
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Card>
        </Stack>
        <InitiativeInclusionNotice
          virtualInitiativeInclusions={virtualInitiativeInclusions}
          mode="add" // or "add"
        />

        <ValidationNotice validationResult={validationResult} />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Annuller
        </Button>
        <Button
          variant="contained"
          onClick={handleAddNode}
          disabled={!formState.type}
        >
          Opret
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNodeDialog;
